import { Box } from '@mui/material';
import { ReactNode } from 'react';

interface HillShapeProps {
  /** Horizontal center of hill in SVG units (0–400) */
  position?: number;
  /** Hill width in SVG units */
  width?: number;
  /** Hill height in SVG units */
  height?: number;
  /** Fill color */
  color: string;
  /** Corner radius for the card body (only used when bodyHeight > 0) */
  borderRadius?: number;
  /** Card body height in SVG units below the hill. 0 = standalone hill bump */
  bodyHeight?: number;
  /** Flat top portion of the hill in SVG units (0 = pointed peak) */
  flatTop?: number;
  /** Content rendered centered on the hill peak */
  children?: ReactNode;
  /** CSS height for the rendered element */
  cssHeight?: number | string;
  /** Extra sx on the wrapper */
  sx?: Record<string, any>;
}

export default function HillShape({
  position = 45,
  width = 90,
  height = 40,
  color,
  borderRadius = 14,
  bodyHeight = 130,
  flatTop = 0,
  children,
  cssHeight,
  sx,
}: HillShapeProps) {
  const left = position - width / 2;
  const right = position + width / 2;
  const r = borderRadius;
  const totalH = height + bodyHeight;

  let path: string;

  if (bodyHeight > 0) {
    // Hill bump on top of rectangular card body
    path = [
      `M 0 ${height}`,
      `C 0 0, ${left + width * 0.22} 0, ${position - flatTop / 2} 0`,
      flatTop > 0 ? `L ${position + flatTop / 2} 0` : '',
      `C ${right - width * 0.22} 0, ${right} ${height}, ${right} ${height}`,
      `L ${400 - r} ${height}`,
      `Q 400 ${height}, 400 ${height + r}`,
      `L 400 ${totalH - r}`,
      `Q 400 ${totalH}, ${400 - r} ${totalH}`,
      `L ${r} ${totalH}`,
      `Q 0 ${totalH}, 0 ${totalH - r}`,
      'Z',
    ].join(' ');
  } else {
    // Standalone hill bump (no rectangle body)
    // Left side: steep edge down; right side: smooth curve
    const cpInset = width * 0.22;
    const leftBase = Math.max(0, left - width * 0.3);
    path = [
      `M ${leftBase} ${height}`,
      `C ${leftBase + width * 0.2} ${height}, ${leftBase + width * 0.2} ${height * 0.6}, ${left} ${height * 0.3}`,
      `C ${left + width * 0.05} ${height * 0.1}, ${left + width * 0.1} 0, ${position - flatTop / 2} 0`,
      flatTop > 0 ? `L ${position + flatTop / 2} 0` : '',
      `Q ${right - width * 0.05} 0, ${right - width * 0.05} ${height * 0.4}`,
      `L ${right} ${height}`,
      'Z',
    ].join(' ');
  }

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <svg
        viewBox={`0 0 400 ${totalH}`}
        preserveAspectRatio="none"
        style={{ display: 'block', width: '100%', height: cssHeight }}
      >
        <path d={path} fill={color} style={{ transition: 'd 0.35s ease-in-out' }} />
      </svg>
      {children && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(position / 400) * 100}%`,
            top: 0,
            transform: 'translateX(-50%)',
            transition: 'left 0.35s ease-in-out',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
